import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toSlugWithFallback } from '@pageboss/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { clearAttempt, currentAttempt } from '@/lib/creation-attempt';
import { writeActiveTenant } from '@/lib/active-tenant';
import { businessApi } from '@/lib/api-paths';
import { locationRoutes } from '@/lib/route-map';

// =====================================================================
// /app/start — the TRANSITIONAL onboarding screen (Phase 1 / T1.6B3).
//
// This is deliberately the plain typed form, not Phase 3's X-ray flow. There is
// no Find-your-business search, no Google or DNS proof, no scan, no diagnosis
// and no plan engine here: those arrive with T3.5/T3.6 and this screen is
// EXTENDED into them rather than replaced.
//
// Three modes, and the mode is explicit on the wire:
//   new           — a member with no business creates their first one
//   add-business  — `?mode=add-business`, an explicit second boundary
//   resume        — `?resume={businessId}`, continue an owned provisional one
//
// The two-step shape is the point (§22). Creating does NOT make the business
// the acting tenant: it is born `provisional`, every operational API refuses
// it, and it becomes `active` only when the member explicitly presses Continue
// to plan. Nothing here activates on form submit, on page load, on resume, or
// on a draft/claim hint.
// =====================================================================

type Mode = 'new' | 'add-business' | 'resume';

interface CreatedTenant {
  businessId: string;
  locationId: string;
}

interface ResumeState {
  businessId: string;
  locationId: string;
  name: string;
}

export function SiteNew() {
  const meState = useMe();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resume');
  const mode: Mode = resumeId ? 'resume' : searchParams.get('mode') === 'add-business' ? 'add-business' : 'new';

  // A prefill hint only. `?draft=` and `?claim=` name content the visitor was
  // looking at; neither proves ownership, selects a tenant, or reaches the
  // server as anything but the typed name the member confirms below.
  const prefillName = searchParams.get('name') ?? '';

  const [name, setName] = useState(prefillName);
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenant, setTenant] = useState<CreatedTenant | null>(null);
  const [resumeFailed, setResumeFailed] = useState(false);
  const [goTo, setGoTo] = useState<string | null>(null);

  const computedSlug = slugTouched ? slug : name.trim() ? toSlugWithFallback(name, 'site') : '';

  // Resume: read the owned provisional business, or fail safely. The server
  // proves OWNER membership and that it is still provisional; a foreign,
  // missing, active or archived id is the same 404, so nothing here branches on
  // which it was.
  useEffect(() => {
    if (!resumeId) return;
    let cancelled = false;
    api
      .get<ResumeState>(businessApi(resumeId, 'start'))
      .then((r) => {
        if (cancelled) return;
        setTenant({ businessId: r.businessId, locationId: r.locationId });
        setName(r.name);
      })
      .catch(() => {
        if (!cancelled) setResumeFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please enter a business name.');
      return;
    }
    setSubmitting(true);
    try {
      // The attempt id is minted once per mode and resent unchanged on retry,
      // so a double-submit converges on ONE business instead of making two.
      const res = await api.post<CreatedTenant>('/api/start', {
        creationId: currentAttempt(mode),
        intent: mode === 'add-business' ? 'add_business' : 'first_business',
        name: name.trim(),
        slug: computedSlug,
      });
      clearAttempt(mode);
      invalidateMe();
      // NOT navigated anywhere yet, and NOT written to `pb.active`: the
      // business is provisional, so it is not the acting tenant.
      setTenant({ businessId: res.businessId, locationId: res.locationId });
    } catch (err) {
      const e = err as Error & { body?: { error?: string; slug?: string } };
      if (e.body?.error === 'slug_taken') {
        setError(`The address "${e.body.slug}" is taken. Try a different one.`);
      } else if (e.body?.error === 'creation_conflict') {
        setError("We couldn't finish creating this. Please reload and try again.");
      } else if (e.body?.error === 'invalid_input') {
        setError('Please check the name and address — letters, numbers and hyphens only.');
      } else {
        setError(e.message);
      }
      setSubmitting(false);
    }
  }

  // The ONE explicit typed_proceed. The server hardcodes the event; this sends
  // no status and no event name, only the intent to proceed.
  const handleProceed = useCallback(async () => {
    if (!tenant) return;
    setActivating(true);
    setError(null);
    try {
      const res = await api.post<{ businessId: string; locationId: string | null }>(
        businessApi(tenant.businessId, 'activate'),
        {},
      );
      const locationId = res.locationId ?? tenant.locationId;
      invalidateMe();
      // Only NOW does it become the remembered tenant: it is active, so it is
      // something the member can actually act on.
      writeActiveTenant({ businessId: res.businessId, locationId });
      setGoTo(locationRoutes.overview(res.businessId, locationId));
    } catch (err) {
      setActivating(false);
      setError(err instanceof Error ? err.message : 'Could not continue. Please try again.');
    }
  }, [tenant]);

  if (goTo) return <Navigate to={goTo} replace />;
  if (meState.kind === 'loading') return null;

  if (resumeFailed) {
    return (
      <main className="min-h-screen bg-background px-6 py-16">
        <div className="mx-auto max-w-xl space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>We couldn't pick that back up</CardTitle>
              <CardDescription>
                That setup isn't waiting to be finished any more. You can start a new one.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/app/start">
                <Button type="button">Start over</Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </main>
    );
  }

  // Step 2 — created (or resumed) and waiting for the explicit proceed.
  if (tenant) {
    return (
      <main className="min-h-screen bg-background px-6 py-16">
        <div className="mx-auto max-w-xl space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{name} is set up</CardTitle>
              <CardDescription>
                Nothing is live yet. Continue when you're ready to pick a plan — you can change any of
                this afterwards.
              </CardDescription>
            </CardHeader>
            {error ? (
              <CardContent>
                <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                  {error}
                </div>
              </CardContent>
            ) : null}
            <CardFooter className="justify-end">
              <Button type="button" onClick={() => void handleProceed()} disabled={activating}>
                {activating ? 'One moment…' : 'Continue to plan'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
    );
  }

  // Step 1 — the typed form.
  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="mx-auto max-w-xl space-y-4">
        <Link
          to="/app"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to dashboard
        </Link>
        <Card>
          <CardHeader>
            <CardTitle>{mode === 'add-business' ? 'Add another business' : 'Set up your business'}</CardTitle>
            <CardDescription>
              Tell us the name. We'll give you an address on{' '}
              <span className="font-mono">web.pageboss.com</span>; you can connect your own domain
              later.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleCreate}>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="business-name">Business name</Label>
                <Input
                  id="business-name"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Coffee"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-slug">Address</Label>
                <div className="flex items-center rounded-md border bg-muted/40">
                  <span className="px-3 text-sm text-muted-foreground">web.pageboss.com/</span>
                  <Input
                    id="site-slug"
                    required
                    maxLength={64}
                    value={computedSlug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value.toLowerCase());
                    }}
                    placeholder="acme-coffee"
                    pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                    className="border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, and hyphens only.
                </p>
              </div>
              {error ? (
                <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                  {error}
                </div>
              ) : null}
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Link to="/app">
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Setting up…' : 'Continue'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </main>
  );
}
